/**
 * IntakeInbox (PROD-3274 DF-04): the F1 patient inbox, canvas "IntakeInbox" of 14 Oct. The drop zone, then one block per
 * batch (heading, five-step strip, table), then three info cards. Nothing here is part of the record until someone adds
 * it: the table only shows what the server holds and offers Review, Add or Disregard per file.
 *
 * The data comes from `useIntakeInbox` (key `['intake-inbox', patientId]`, polled every 3 s while a file is reading).
 */
import { useState } from 'react';
import {
  Banner,
  Button,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@scripsteam/scrips-signal-ds';
import { useDisregardInboxDocument, useIntakeInbox } from '../hooks/use-intake-inbox';
import { batchHeading, groupByBatch, inboxActionError, stepStrip } from '../lib/inbox-view';
import type { InboxDocument } from '../types';
import { InboxDisregardDialog } from './inbox-disregard-dialog';
import { InboxRow } from './inbox-row';
import { IntakeDropZone } from './intake-drop-zone';

export interface IntakeInboxProps {
  patientId: string;
  patientFirstName: string;
  /** Passed to the drop zone: a one-shot request to open its file picker, and the call that consumes it. */
  openPicker?: boolean;
  onPickerOpened?: () => void;
}

const COLUMNS = ['File', 'Recognized as', 'Patient check', 'Goes to', 'Status', 'Action'];

const FOOTER_CARDS = [
  {
    title: 'Review values',
    text: 'Opens the single review step: the report beside its values, fix inline, Add or Disregard per row, one Confirm.',
  },
  {
    title: 'Each lab keeps its own ranges',
    text: 'Values from different labs stay on separate trend lines. Printed ranges are saved with each value.',
  },
  { title: 'Disregard', text: 'Removes the file from the inbox. It is kept in the audit record, never deleted.' },
];

function StepStrip({ docs }: { docs: InboxDocument[] }) {
  const steps = stepStrip(docs);
  return (
    <ol aria-label="Reading steps for this batch" data-slot="intake-steps-strip">
      {steps.map((s, i) => (
        <li
          key={s.key}
          data-slot="intake-strip-step"
          data-first={i === 0 ? 'true' : undefined}
          data-last={i === steps.length - 1 ? 'true' : undefined}
        >
          <span data-slot="intake-strip-title">{s.title}</span>
          {s.value ? <span data-slot="intake-strip-value">{s.value}</span> : null}
          <span data-slot="intake-strip-desc">{s.description}</span>
        </li>
      ))}
    </ol>
  );
}

export function IntakeInbox({ patientId, patientFirstName, openPicker, onPickerOpened }: IntakeInboxProps) {
  const inbox = useIntakeInbox(patientId);
  const disregard = useDisregardInboxDocument(patientId);
  const [disregarding, setDisregarding] = useState<InboxDocument | null>(null);
  const docs = inbox.data ?? [];

  return (
    <div data-slot="intake-inbox">
      <IntakeDropZone
        patientId={patientId}
        patientFirstName={patientFirstName}
        openPicker={openPicker}
        onPickerOpened={onPickerOpened}
      />

      {inbox.isLoading ? (
        <div aria-busy="true" aria-label="Loading the inbox" className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : inbox.isError && !inbox.data ? (
        <Banner
          variant="error"
          title="The inbox could not be loaded."
          action={
            <Button size="sm" variant="secondary" onClick={() => void inbox.refetch()}>
              Try again
            </Button>
          }
        >
          Check the connection and try again.
        </Banner>
      ) : docs.length === 0 ? (
        <EmptyState
          icon="inbox"
          title={`No files waiting. Drop files above to add them for ${patientFirstName}.`}
        />
      ) : (
        <>
          {inbox.isError ? (
            // A refresh failed but the last list is still here: keep it, say so quietly, and offer a refresh.
            <p role="status" data-slot="intake-stale" data-testid="inbox-stale">
              The inbox could not be refreshed. This list may be out of date.
              <Button size="sm" variant="tertiary" onClick={() => void inbox.refetch()}>
                Refresh
              </Button>
            </p>
          ) : null}
          {groupByBatch(docs).map((batch) => (
          <section key={batch.batchId} aria-label={batchHeading(batch.documents)} data-slot="intake-batch">
            <h3 data-slot="intake-batch-heading">{batchHeading(batch.documents)}</h3>
            <StepStrip docs={batch.documents} />
            <div data-slot="intake-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((c) => (
                      <TableHead key={c}>{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batch.documents.map((d) => (
                    <InboxRow
                      key={d.documentId}
                      doc={d}
                      patientId={patientId}
                      patientFirstName={patientFirstName}
                      onDisregard={(x) => {
                        disregard.reset();
                        setDisregarding(x);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
          ))}
        </>
      )}

      <div data-slot="intake-cards">
        {FOOTER_CARDS.map((c) => (
          <div key={c.title} data-slot="intake-card">
            <div data-slot="intake-card-title">{c.title}</div>
            <div data-slot="intake-card-text">{c.text}</div>
          </div>
        ))}
      </div>

      <InboxDisregardDialog
        key={disregarding?.documentId ?? 'none'}
        fileName={disregarding?.fileName ?? null}
        pending={disregard.isPending}
        failure={disregard.isError ? inboxActionError(disregard.error, 'disregard') : null}
        onCancel={() => {
          // The Disregard error lives here, outside the dialog: clear it on close as well as on open.
          disregard.reset();
          setDisregarding(null);
        }}
        onConfirm={(reason, note) =>
          disregarding &&
          disregard.mutate(
            { documentId: disregarding.documentId, reason, note },
            { onSuccess: () => setDisregarding(null) },
          )
        }
      />
    </div>
  );
}
